// frontend/src/components/GiftPurchaseInterface.js
import React, { useState, useEffect, useRef } from 'react'; // Добавляем useRef
import { useData } from '../context/DataContext';
import './GiftPurchaseInterface.css'; 

const GiftPurchaseInterface = () => {
  const { content: allContent, fetchContentData } = useData();
  const [giftableItems, setGiftableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [giftCovers, setGiftCovers] = useState([]);
  const [loadingCovers, setLoadingCovers] = useState(true);
  const [errorCovers, setErrorCovers] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  const [price, setPrice] = useState(0);

  const [activeCoverId, setActiveCoverId] = useState(null); // Для отслеживания активного слайда в карусели
  const coversContainerRef = useRef(null); // Ref для контейнера обложек
  const coverRefs = useRef({}); // Refs для отдельных элементов обложек

  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null); // Для отображения результата (например, URL платежа или сообщение)
  const [purchaseError, setPurchaseError] = useState(null);

  const fetchActiveGiftCovers = async () => {
    setLoadingCovers(true);
    setErrorCovers(null);
    try {
      const response = await fetch('/api/gifts/covers'); 
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка загрузки обложек' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setGiftCovers(data || []);
      if (data && data.length > 0) {
        setSelectedCover(data[0]);
        setActiveCoverId(data[0]._id); // Устанавливаем первую обложку как активную
        // Предзагрузка изображений обложек
        data.forEach(cover => {
          if (cover.url) {
            new Image().src = cover.url;
          }
        });
      }
    } catch (err) {
      console.error('Ошибка при загрузке обложек для подарков:', err);
      setErrorCovers(err.message);
      setGiftCovers([]);
    } finally {
      setLoadingCovers(false);
    }
  };

  useEffect(() => {
    // console.log('[GiftPurchaseInterface] Initializing: Forcing data fetch for gift context.'); // Откатываем лог
    fetchContentData('all'); // Откатываем к простому вызову
    fetchActiveGiftCovers();
  }, [fetchContentData]); // fetchContentData теперь должен быть стабильным из-за useCallback

  useEffect(() => {
    console.log('[GiftPurchaseInterface] useEffect for allContent triggered. Current allContent:', allContent);
    if (allContent && allContent.length > 0) {
      console.log(`[GiftPurchaseInterface] Checking allContent (${allContent.length} items) for giftable status:`);
      allContent.forEach((item, index) => {
        console.log(`  Item ${index + 1}: title='${item.title}', price=${item.price}, isGiftable=${item.isGiftable}, isFree=${item.isFree}, _id=${item._id}`);
      });
    } else {
      console.log('[GiftPurchaseInterface] allContent is currently empty or null.');
    }
    const giftable = allContent.filter(item => item.isGiftable && !item.isFree);
    console.log('[GiftPurchaseInterface] giftableItems after filter:', giftable.map(item => ({ title: item.title, _id: item._id, isGiftable: item.isGiftable, isFree: item.isFree })));
    setGiftableItems(giftable);
  }, [allContent]);

  useEffect(() => {
    if (selectedItem) {
      const item = giftableItems.find(i => i._id === selectedItem);
      if (item) {
        // Для подарков используем "чистую" цену из originalPriceFromDB, 
        // которую теперь передает бэкенд.
        // Если по какой-то причине этого поля нет, используем item.price как запасной вариант.
        const priceToDisplay = item.originalPriceFromDB !== undefined ? item.originalPriceFromDB : item.price;
        const displayPrice = parseFloat(priceToDisplay);
        setPrice(displayPrice);
      }
    } else {
      setPrice(0);
    }
  }, [selectedItem, giftableItems]);

  const handleItemChange = (event) => {
    setSelectedItem(event.target.value);
    setPurchaseResult(null); // Сбрасываем результат предыдущей попытки
    setPurchaseError(null);
  };

  const handleCoverSelect = (cover) => {
    setSelectedCover(cover);
    setActiveCoverId(cover._id); // Обновляем активный ID при выборе
    setPurchaseResult(null);
    setPurchaseError(null);

    // Прокрутка к выбранному элементу
    if (coverRefs.current[cover._id]) {
      coverRefs.current[cover._id].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  // Эффект для определения активного слайда при прокрутке
  useEffect(() => {
    if (!giftCovers || giftCovers.length === 0 || !coversContainerRef.current || loadingCovers) {
      return;
    }

    const container = coversContainerRef.current;
    let animationFrame = null;
    
    const updateActiveSlide = () => {
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      
      let closestElement = null;
      let closestDistance = Infinity;
      
      // Находим все элементы обложек
      const elements = container.querySelectorAll('.gift-cover-item');
      elements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        const elementCenter = elementRect.left + elementRect.width / 2;
        const distance = Math.abs(elementCenter - containerCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestElement = { 
            id: element.dataset.coverId, 
            element 
          };
        }
      });
      
      // Обновляем активный элемент
      if (closestElement && closestElement.id) {
        const newPotentialActiveId = closestElement.id; // ID элемента, который сейчас в центре

        // Обновляем selectedCover, если центральный элемент изменился
        // или если selectedCover еще не соответствует текущему newPotentialActiveId
        const newSelectedCoverCandidate = giftCovers.find(cover => cover._id === newPotentialActiveId);
        if (newSelectedCoverCandidate && selectedCover?._id !== newSelectedCoverCandidate._id) {
          setSelectedCover(newSelectedCoverCandidate);
        }
        
        // Обновляем activeCoverId, если он изменился.
        // Это важно для стилей 'is-active-slide' и как зависимость useEffect.
        if (activeCoverId !== newPotentialActiveId) {
          setActiveCoverId(newPotentialActiveId);
        }
      }
    };

    // Обработчик для scroll событий с использованием requestAnimationFrame
    const handleScroll = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(updateActiveSlide);
    };

    // Также отслеживаем touch события для мобильных устройств
    // let touchStartX = 0; // ESLint: 'touchStartX' is assigned a value but never used.
    const handleTouchStart = (e) => {
      // touchStartX = e.touches[0].clientX; // Переменная не используется, удаляем или комментируем
    };

    const handleTouchMove = () => {
      handleScroll();
    };

    const handleTouchEnd = () => {
      // Задержка для завершения инерционной прокрутки
      setTimeout(updateActiveSlide, 100);
      setTimeout(updateActiveSlide, 300);
    };

    // Инициализация - определяем начальный активный элемент
    setTimeout(updateActiveSlide, 100);
    
    // Добавляем обработчики событий
    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [giftCovers, loadingCovers, activeCoverId, selectedCover]); // Добавлена зависимость selectedCover


  const handlePurchase = async () => {
    if (!selectedItem) {
      alert('Пожалуйста, выберите материал для подарка.');
      return;
    }
    if (!selectedCover) {
      alert('Пожалуйста, выберите обложку для подарка.');
      return;
    }

    setIsPurchasing(true);
    setPurchaseResult(null);
    setPurchaseError(null);

    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch('/api/payments/create', { // Исправленный эндпоинт создания платежа
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId,
        },
        body: JSON.stringify({
          contentId: selectedItem,
          isGiftPurchase: true,
          giftCoverImageUrl: selectedCover.url,
          // returnUrl можно не передавать, если он всегда генерируется на бэке
          // или передать, если для подарков он должен быть другим
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error ${response.status}`);
      }
      
      if (data.payment && data.payment.paymentUrl) {
        // Если есть URL для оплаты (Yookassa, Prodamus), перенаправляем
        // Используем тот же подход, что и в ContentCard.js
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.openLink(data.payment.paymentUrl);
        } else {
          // Резервный вариант, если Telegram WebApp недоступен
          window.open(data.payment.paymentUrl, '_blank');
          console.warn('[GiftPurchaseInterface] Telegram WebApp API not found, using window.open as fallback.');
        }
        // Пользователь будет перенаправлен во внешний браузер.
        // Сообщение "Перенаправляем на страницу оплаты..." больше не нужно, так как интерфейс приложения остается видимым.
        // setPurchaseResult({ message: "Перенаправляем на страницу оплаты..." }); // Убираем эту строку
      } else if (data.payment && data.payment.provider === 'manual') {
        // Если это ручной платеж
        setPurchaseResult({ 
            message: "Запрос на покупку подарка отправлен администратору. После подтверждения оплаты, вы получите ссылку на подарок в Telegram.",
            manualPayment: true 
        });
      } else {
        // Неожиданный ответ
        throw new Error('Не удалось получить ссылку на оплату или информацию о ручном платеже.');
      }

    } catch (err) {
      console.error('Ошибка при инициации покупки подарка:', err);
      setPurchaseError(err.message || 'Не удалось инициировать покупку подарка.');
    } finally {
      setIsPurchasing(false);
    }
  };
  
  // Если есть результат (например, сообщение о ручном платеже), показываем его
  // Для редиректа на платежную систему, пользователь уже будет перенаправлен
  if (purchaseResult && purchaseResult.manualPayment) {
    return (
      <div className="gift-purchase-interface gift-purchase-notice">
        <h2>Запрос отправлен</h2>
        <p>{purchaseResult.message}</p>
        <button onClick={() => { setPurchaseResult(null); setSelectedItem(''); }} className="gift-purchase-button">
          Купить еще подарок
        </button>
      </div>
    );
  }
  // Блок для сообщения "Перенаправляем на страницу оплаты..." был удален,
  // так как setPurchaseResult с этим сообщением больше не вызывается.
  // if (purchaseResult && purchaseResult.message === "Перенаправляем на страницу оплаты...") {
  // return (
  // <div className="gift-purchase-interface gift-purchase-notice">
  // <h2>Перенаправление...</h2>
  // <p>{purchaseResult.message}</p>
  // <div className="loading-spinner" style={{margin: '20px auto'}}></div>
  // </div>
  // );
  // }


  return (
    <div className="gift-purchase-interface">
      <h2>Сделай подарок</h2>
      <p className="gift-description">Вы можете подарить любой материал другу. После оплаты вам в бот придет специальная ссылка, по которой ваш друг сможет получить свой подарок.</p>
      
      <div className="gift-form-group">
        <label htmlFor="gift-item-select">Выберите что дарить:</label>
        <select 
          id="gift-item-select" 
          value={selectedItem} 
          onChange={handleItemChange} 
          // disabled={isPurchasing || (giftableItems.length === 0 && allContent.length > 0 && !isPurchasing)} // Временно убираем disabled
        >
          <option value="">-- Выберите материал --</option>
          {giftableItems.length > 0 ? (
            giftableItems.map(item => (
              <option key={item._id} value={item._id}>
                {item.title}
              </option>
            ))
          ) : (
            <option value="" disabled>
              {allContent.length > 0 ? "-- Нет материалов для подарка (проверьте фильтры) --" : "-- Загрузка материалов... --"}
            </option>
          )}
        </select>
      </div>

      {/* Убираем условие selectedItem, чтобы интерфейс был виден сразу */}
      <div className="gift-details-wrapper"> {/* Обертка для анимации */}
          <div className="gift-form-group">
            <label>Выберите обложку:</label>
            {loadingCovers ? <p>Загрузка обложек...</p> : errorCovers ? <p className="error-message">Ошибка загрузки обложек: {errorCovers}</p> : (
              <div className="gift-covers-container" ref={coversContainerRef}>
                {giftCovers.length === 0 && <p>Нет доступных обложек.</p>}
                {giftCovers.map((cover) => (
                  <div
                    key={cover._id}
                    ref={el => coverRefs.current[cover._id] = el} // Привязываем ref к каждому элементу
                    data-cover-id={cover._id} // Добавляем data-атрибут для IntersectionObserver
                    className={`gift-cover-item ${activeCoverId === cover._id ? 'is-active-slide' : ''}`}
                    onClick={() => !isPurchasing && handleCoverSelect(cover)} // Выбор по клику остается для удобства
                  >
                    <img
                      src={cover.url}
                      alt={cover.name || `Обложка ${cover._id}`}
                      className={`gift-cover-image ${selectedCover?._id === cover._id ? 'selected' : ''} ${isPurchasing ? 'disabled' : ''}`}
                      // onClick обработчик перенесен на родительский div
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="gift-purchase-footer">
            <p className={`gift-price ${(!selectedItem || price <= 0) ? 'nopricestyle1' : ''}`}>
              {selectedItem && price > 0 ? `Цена: ${price} руб.` : 'Цена:'}
            </p>
            <button onClick={handlePurchase} className="gift-purchase-button" disabled={isPurchasing || !selectedItem || !selectedCover}>
              {isPurchasing ? 'Обработка...' : 'Оплатить'}
            </button>
          </div>
          {purchaseError && <p className="error-message" style={{textAlign: 'center', marginTop: '10px'}}>{purchaseError}</p>}
          <p className="gift-note">После оплаты бот пришлет вам ссылку-подарок для вашего друга.</p>
        </div> {/* Закрытие gift-details-wrapper */}
      {/* )} */} {/* Закрывающий элемент для удаленного условия */}
    </div>
  );
};

export default GiftPurchaseInterface;
